import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import type { ReviewSummary } from './types';

export type ReviewFilter = 'active' | 'failed' | 'converged' | 'all';

interface ReviewFilterBarProps {
  filter: ReviewFilter;
  onFilterChange: (filter: ReviewFilter) => void;
  search: string;
  onSearchChange: (search: string) => void;
  /** Total count across all filters, used to render "(X of Y)" summary. */
  totalCount: number;
  /** Count after applying the current filter + search. */
  visibleCount: number;
}

const FILTERS: Array<{ id: ReviewFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'failed', label: 'Failed' },
  { id: 'converged', label: 'Converged' },
  { id: 'all', label: 'All' },
];

export function ReviewFilterBar({
  filter,
  onFilterChange,
  search,
  onSearchChange,
  totalCount,
  visibleCount,
}: ReviewFilterBarProps) {
  const [localSearch, setLocalSearch] = useState(search);

  useEffect(() => {
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    const handle = setTimeout(() => {
      if (localSearch !== search) onSearchChange(localSearch);
    }, 150);
    return () => clearTimeout(handle);
  }, [localSearch, search, onSearchChange]);

  return (
    <div
      data-testid="review-filter-bar"
      className="flex flex-wrap items-center justify-between gap-3 mb-4"
    >
      <div className="flex items-center gap-2">
        {FILTERS.map((f) => {
          const isActive = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              data-testid={`filter-chip-${f.id}`}
              aria-pressed={isActive}
              onClick={() => onFilterChange(f.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                isActive
                  ? 'bg-teal-500 text-white border-teal-500'
                  : 'bg-transparent text-gray-300 border-gray-700 hover:bg-gray-800 hover:border-gray-600'
              }`}
            >
              {f.label}
            </button>
          );
        })}
        <span
          data-testid="review-filter-count"
          className="ml-2 text-xs text-gray-500"
        >
          {visibleCount} of {totalCount}
        </span>
      </div>
      <div className="relative w-64">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
        <input
          type="text"
          data-testid="review-filter-search"
          placeholder="Search repo, PR number, or title…"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-3 py-1.5 text-sm text-gray-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
        />
      </div>
    </div>
  );
}

/** Number of milliseconds in 24 hours. */
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function hasEnrichment(r: ReviewSummary): boolean {
  // A failed review with any recorded findings is still worth triaging even if
  // the step/cost/duration telemetry is missing, so include findings counts in
  // the "has enrichment" predicate.
  const totalFindings =
    r.findings.open + r.findings.fixed + r.findings.dismissed + (r.findings.deferred ?? 0);
  return (
    (typeof r.stepsCompleted === 'number' && r.stepsCompleted > 0) ||
    (typeof r.totalCostUsd === 'number' && r.totalCostUsd > 0) ||
    (typeof r.totalDurationMs === 'number' && r.totalDurationMs > 0) ||
    totalFindings > 0
  );
}

function isArchivedFailure(r: ReviewSummary, now: number): boolean {
  if (r.state !== 'failed') return false;
  if (hasEnrichment(r)) return false;
  const last = new Date(r.lastActivityAt).getTime();
  return now - last > ONE_DAY_MS;
}

/**
 * Pure filter predicate. Exposed so callers and tests can use the same logic.
 *
 * - `active`: pending/fetching/reviewing/converging/paused; or recent failures
 *   that still have checkpoints worth triaging. Archived empty-failures hidden.
 * - `failed`: everything with `state === 'failed'`, including archived.
 * - `converged`: converged / completed / max_rounds_reached.
 * - `all`: no filter.
 * Search matches `repo`, `prNumber` (as string), or a substring of `prTitle`,
 * case-insensitive. Including `repo` lets users filter by project slug once
 * the Command Centre aggregates reviews across multiple repos.
 */
export function filterReviews(
  reviews: ReviewSummary[],
  filter: ReviewFilter,
  search: string,
  now: number = Date.now(),
): ReviewSummary[] {
  const query = search.trim().toLowerCase();
  return reviews.filter((r) => {
    switch (filter) {
      case 'active': {
        const isActiveState =
          r.state === 'pending' ||
          r.state === 'fetching' ||
          r.state === 'reviewing' ||
          r.state === 'converging' ||
          r.state === 'paused' ||
          r.state === 'idle';
        if (isActiveState) break;
        if (r.state === 'failed' && !isArchivedFailure(r, now)) break;
        return false;
      }
      case 'failed':
        if (r.state !== 'failed') return false;
        break;
      case 'converged':
        if (
          r.state !== 'converged' &&
          r.state !== 'completed' &&
          r.state !== 'max_rounds_reached'
        ) {
          return false;
        }
        break;
      case 'all':
      default:
        break;
    }
    if (!query) return true;
    const hay = `${r.repo ?? ''} ${r.prNumber} ${r.prTitle ?? ''}`.toLowerCase();
    return hay.includes(query);
  });
}
